import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import '../Auth.css';
import './PaymentRegister.css';
import logo from '../../../assets/images/seedxpert-light.png';
import LanguageSwitcher from '../../../components/LanguageSwitcher/LanguageSwitcher';

const PaymentRegister = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();

  // URL'den paket parametresini kontrol et ve başlangıç step'ini belirle
  const packageParam = searchParams.get('package');
  const packageAliases = {
    basic: 'basic', '1': 'basic', '1rapor': 'basic', '1report': 'basic',
    standard: 'standard', '25': 'standard', '25rapor': 'standard', '25report': 'standard',
    premium: 'premium', unlimited: 'premium', '50': 'premium', '50rapor': 'premium', '50report': 'premium',
    enterprise100: 'enterprise100', package100: 'enterprise100', '100': 'enterprise100', '100rapor': 'enterprise100', '100report': 'enterprise100', '100reports': 'enterprise100',
    enterprise250: 'enterprise250', package250: 'enterprise250', '250': 'enterprise250', '250rapor': 'enterprise250', '250report': 'enterprise250', '250reports': 'enterprise250',
    enterprise400: 'enterprise400', package400: 'enterprise400', '400': 'enterprise400', '400rapor': 'enterprise400', '400report': 'enterprise400', '400reports': 'enterprise400'
  };
  const packageParamKey = packageParam?.toLowerCase().replace(/[\s_-]/g, '');
  const normalizedPackageParam = packageAliases[packageParamKey]
    || (packageParamKey?.includes('400') ? 'enterprise400' : null)
    || (packageParamKey?.includes('250') ? 'enterprise250' : null)
    || (packageParamKey?.includes('100') ? 'enterprise100' : null)
    || (packageParamKey?.includes('50') ? 'premium' : null)
    || (packageParamKey?.includes('25') ? 'standard' : null)
    || packageParam;
  const initialStep = packageParam ? 2 : 1;

  const [currentStep, setCurrentStep] = useState(initialStep); // 1: Paket Seçimi, 2: Kayıt, 3: Ödeme
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'Turkey'
    }
  });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);
  const [showGdprModal, setShowGdprModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: 'error', message: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [iframeToken, setIframeToken] = useState(null);
  const [iframeOrderId, setIframeOrderId] = useState(null);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [usdToTryRate, setUsdToTryRate] = useState(40); // USD to TRY kuru (varsayılan: 40)
  const navigate = useNavigate();
  const currentLanguage = i18n.language || 'tr';
  const isTurkishCurrency = currentLanguage.startsWith('tr');

  const packageContent = isTurkishCurrency ? {
    basic: {
      name: '1 Rapor',
      description: '1 Girişim Analizi ve Değerleme Raporu',
      features: [
        '1 girişim analizi ve değerleme raporu',
        'DCF + Berkus değerleme yöntemleri',
        'Risk analizi',
        'Çoklu para biriminde değerleme',
        'Yatırımcı raporu oluşturma ve paylaşma',
        'Finansal/veri çıktılarının indirilmesi',
        '1 yıl platform erişimi',
        '15 dk platform kullanım eğitimi'
      ]
    },
    standard: {
      name: '25 Rapor',
      description: '25 Girişim Analizi ve Değerleme Raporu',
      features: [
        '25 girişim analizi ve değerleme raporu',
        'DCF + Berkus değerleme yöntemleri',
        'Risk analizi',
        'Çoklu para biriminde değerleme',
        'Finansal/veri çıktılarının indirilmesi',
        '1 yıl platform erişimi',
        '1 saatlik online platform kullanım eğitimi',
        'Tüm girişim raporlarını tek ekrandan yönetme'
      ]
    },
    premium: {
      name: '50 Rapor',
      description: '50 Girişim Analizi ve Değerleme Raporu',
      features: [
        '50 girişim analizi ve değerleme raporu',
        'DCF + Berkus değerleme yöntemleri',
        'Risk analizi',
        'Çoklu para biriminde değerleme',
        'Finansal/veri çıktılarının indirilmesi',
        '1 yıl platform erişimi',
        '1 saatlik online platform kullanım eğitimi',
        '2 saat soru-cevap mentorluk',
        'Tüm girişim raporlarını tek ekrandan yönetme'
      ]
    },
    enterprise100: {
      name: '100 Rapor',
      description: '100 Girişim Analizi ve Değerleme Raporu',
      features: [
        '100 girişim analizi ve değerleme raporu', 'DCF + Berkus değerleme yöntemleri', 'Risk analizi',
        'Çoklu para biriminde değerleme', 'Finansal/veri çıktılarının indirilmesi', '2 yıl platform erişimi',
        '2 saatlik online platform kullanım eğitimi', '1 saat soru-cevap mentorluk',
        'Tüm girişim raporlarını tek ekrandan yönetme'
      ]
    },
    enterprise250: {
      name: '250 Rapor',
      description: '250 Girişim Analizi ve Değerleme Raporu',
      features: [
        '250 girişim analizi ve değerleme raporu', 'DCF + Berkus değerleme yöntemleri', 'Risk analizi',
        'Çoklu para biriminde değerleme', 'Finansal/veri çıktılarının indirilmesi', '2 yıl platform erişimi',
        '2 saatlik online platform kullanım eğitimi', '2 saat soru-cevap mentorluk',
        'Tüm girişim raporlarını tek ekrandan yönetme'
      ]
    },
    enterprise400: {
      name: '400 Rapor',
      description: '400 Girişim Analizi ve Değerleme Raporu',
      features: [
        '400 girişim analizi ve değerleme raporu', 'DCF + Berkus değerleme yöntemleri', 'Risk analizi',
        'Çoklu para biriminde değerleme', 'Finansal/veri çıktılarının indirilmesi', '2 yıl platform erişimi',
        '2 saatlik online platform kullanım eğitimi', '3 saat soru-cevap mentorluk',
        'Tüm girişim raporlarını tek ekrandan yönetme'
      ]
    }
  } : {
    basic: {
      name: '1 Report',
      description: '1 Startup Analysis and Valuation Report',
      features: [
        '1 startup analysis and valuation report',
        'DCF + Berkus valuation methods',
        'Risk analysis',
        'Valuation in multiple currencies',
        'Create and share investor reports',
        'Download financial/data outputs',
        '1-year platform access',
        '15-minute platform usage training'
      ]
    },
    standard: {
      name: '25 Reports',
      description: '25 Startup Analysis and Valuation Reports',
      features: [
        '25 startup analysis and valuation reports',
        'DCF + Berkus valuation methods',
        'Risk analysis',
        'Valuation in multiple currencies',
        'Download financial/data outputs',
        '1-year platform access',
        '1-hour online platform usage training',
        'Manage all startup reports from one screen'
      ]
    },
    premium: {
      name: '50 Reports',
      description: '50 Startup Analysis and Valuation Reports',
      features: [
        '50 startup analysis and valuation reports',
        'DCF + Berkus valuation methods',
        'Risk analysis',
        'Valuation in multiple currencies',
        'Download financial/data outputs',
        '1-year platform access',
        '1-hour online platform usage training',
        '2-hour Q&A mentoring',
        'Manage all startup reports from one screen'
      ]
    },
    enterprise100: {
      name: '100 Reports',
      description: '100 Startup Analysis and Valuation Reports',
      features: [
        '100 startup analysis and valuation reports', 'DCF + Berkus valuation methods', 'Risk analysis',
        'Valuation in multiple currencies', 'Download financial/data outputs', '2-year platform access',
        '2-hour online platform usage training', '1-hour Q&A mentoring',
        'Manage all startup reports from one screen'
      ]
    },
    enterprise250: {
      name: '250 Reports',
      description: '250 Startup Analysis and Valuation Reports',
      features: [
        '250 startup analysis and valuation reports', 'DCF + Berkus valuation methods', 'Risk analysis',
        'Valuation in multiple currencies', 'Download financial/data outputs', '2-year platform access',
        '2-hour online platform usage training', '2-hour Q&A mentoring',
        'Manage all startup reports from one screen'
      ]
    },
    enterprise400: {
      name: '400 Reports',
      description: '400 Startup Analysis and Valuation Reports',
      features: [
        '400 startup analysis and valuation reports', 'DCF + Berkus valuation methods', 'Risk analysis',
        'Valuation in multiple currencies', 'Download financial/data outputs', '2-year platform access',
        '2-hour online platform usage training', '3-hour Q&A mentoring',
        'Manage all startup reports from one screen'
      ]
    }
  };

  // Packages
  const packages = [
    {
      id: 'basic',
      name: packageContent.basic.name,
      description: packageContent.basic.description,

      //TL
      price: 2500,
      usdPrice: 62.5,

      reports: 1,
      features: packageContent.basic.features,
      popular: false
    },
    {
      id: 'standard',
      name: packageContent.standard.name,
      description: packageContent.standard.description,

      //TL
      price: 50000,
      //USD
      usdPrice: 1250,

      reports: 25,
      unitPrice: isTurkishCurrency ? 'Birim fiyat: ₺2.000' : 'Unit price: $50',
      features: packageContent.standard.features,
      popular: false
    },
    {
      id: 'premium',
      name: packageContent.premium.name,
      description: packageContent.premium.description,

      //TL
      price: 87500,
      //USD
      usdPrice: 2187.5,

      reports: 50,
      unitPrice: isTurkishCurrency ? 'Birim fiyat: ₺1.750' : 'Unit price: $43.75',
      features: packageContent.premium.features,
      popular: false
    },
    {
      id: 'enterprise100',
      name: packageContent.enterprise100.name,
      description: packageContent.enterprise100.description,
      price: 150000,
      usdPrice: 3750,
      reports: 100,
      unitPrice: isTurkishCurrency ? 'Birim fiyat: ₺1.500' : 'Unit price: $37.50',
      features: packageContent.enterprise100.features,
      popular: false
    },
    {
      id: 'enterprise250',
      name: packageContent.enterprise250.name,
      description: packageContent.enterprise250.description,
      price: 300000,
      usdPrice: 7500,
      reports: 250,
      unitPrice: isTurkishCurrency ? 'Birim fiyat: ₺1.200' : 'Unit price: $30',
      features: packageContent.enterprise250.features,
      popular: false
    },
    {
      id: 'enterprise400',
      name: packageContent.enterprise400.name,
      description: packageContent.enterprise400.description,
      price: 400000,
      usdPrice: 10000,
      reports: 400,
      unitPrice: isTurkishCurrency ? 'Birim fiyat: ₺1.000' : 'Unit price: $25',
      features: packageContent.enterprise400.features,
      popular: false
    }
  ];

  // URL'den paket parametresini al ve varsayılan paketi seç
  useEffect(() => {
    if (packageParam) {
      const pkg = packages.find(p => p.id === normalizedPackageParam);
      if (pkg) {
        setSelectedPackage(pkg);
      } else {
        setCurrentStep(1);
        setSelectedPackage(null);
      }
    }
  }, [normalizedPackageParam, currentLanguage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  useEffect(() => {
    const handlePaymentSuccess = (event) => {
      if (
        event.origin !== window.location.origin
        || event.data?.type !== 'SEEDXPERT_PAYMENT_SUCCESS'
      ) {
        return;
      }

      event.source?.postMessage(
        { type: 'SEEDXPERT_PAYMENT_SUCCESS_ACK' },
        event.origin
      );
      setTimeout(() => {
        setPaymentComplete(true);
        setShowIframe(false);
        setIframeLoading(false);
      }, 2000);
    };

    window.addEventListener('message', handlePaymentSuccess);
    return () => window.removeEventListener('message', handlePaymentSuccess);
  }, []);

  // USD to TRY kuru çek
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        const rate = data.rates.TRY;
        if (rate && rate > 0) {
          setUsdToTryRate(rate);
          console.log('USD to TRY rate fetched:', rate);
        }
      } catch (error) {
        console.error('Kur bilgisi alınamadı, varsayılan 40 TL kullanılıyor:', error);
      }
    };

    fetchExchangeRate();
  }, []);


  // Türkiye/Azerbaycan için KVKK/GDPR seçimi
  const isTurkey = formData.address.country === 'Turkey' || formData.address.country === 'Azerbaijan';

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const requiredConsent = isTurkey ? kvkkAccepted : gdprAccepted;
    if (!privacyAccepted || !requiredConsent) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.acceptAgreements') });
      setShowModal(true);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.passwordMismatch') });
      setShowModal(true);
      return false;
    }
    if (formData.password.length < 6) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.passwordLength') });
      setShowModal(true);
      return false;
    }
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.nameRequired') });
      setShowModal(true);
      return false;
    }
    if (!formData.phone.trim()) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.phoneRequired') });
      setShowModal(true);
      return false;
    }
    return true;
  };

  const handlePackageSelection = (pkg) => {
    setSelectedPackage(pkg);
  };

  const handlePackageStepNext = () => {
    if (!selectedPackage) {
      setModalConfig({ type: 'error', message: t('paymentRegister.errors.selectPackage') });
      setShowModal(true);
      return;
    }
    setCurrentStep(2);
    setError('');
  };

  const handleRegisterStep = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      // Önce kullanıcı kaydını kontrol et
      const registerResponse = await axios.post('/api/auth/register', {
        ...formData,
        language: currentLanguage,
        paymentRegistration: true,
        privacyAccepted: true,
        kvkkAccepted: isTurkey ? kvkkAccepted : false,
        gdprAccepted: !isTurkey ? gdprAccepted : false
      });

      // Email doğrulama mesajını göster
      setSuccess(registerResponse.data.msg);

      // Kayıt başarılı ise adım 3'e geç ve ödeme işlemini başlat
      setCurrentStep(3);
      handlePaymentStep(registerResponse.data.userId);

    } catch (err) {
      // If there is an error during registration, stay on step 2 and show error
      setModalConfig({
        type: 'error',
        message: err.response?.data?.msg || t('paymentRegister.errors.registrationError')
      });
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentStep = async (userId) => {
    setIsLoading(true);
    setError('');

    try {
      // Fiyatı TL cinsinden hesapla (PayTR sadece TL kabul eder)
      let priceInTL;
      let originalCurrency;
      let finalPrice;

      if (isTurkishCurrency) {
        // Türkçe: Direkt TL fiyatı + KDV
        priceInTL = selectedPackage.price;
        originalCurrency = 'TL';
        finalPrice = priceInTL * 1.2; // KDV dahil
      } else {
        // İngilizce: USD'yi TL'ye çevir (KDV yok)
        priceInTL = selectedPackage.usdPrice;
        originalCurrency = 'USD';
        finalPrice = priceInTL; // KDV dahil değil
      }

      console.log('Payment Info:', {
        isTurkishCurrency,
        originalCurrency,
        originalPrice: isTurkishCurrency ? selectedPackage.price : selectedPackage.usdPrice,
        usdToTryRate,
        priceInTL,
        hasVAT: isTurkishCurrency,
        finalPrice,
        packageName: selectedPackage.name
      });

      const paymentResponse = await axios.post('/api/payment/create-payment', {
        packageId: selectedPackage.id,
        packageName: selectedPackage.name,
        price: finalPrice, // TL ise KDV dahil, USD ise KDV yok
        curr: originalCurrency, // PayTR'ye her zaman TL gönder
        originalCurrency: originalCurrency, // Kayıt için orijinal para birimi
        reports: selectedPackage.reports,
        userId: userId,
        userEmail: formData.email,
        userAddress: `${formData.address.state}, ${formData.address.country}`,
        userPhone: formData.phone,
        userName: `${formData.firstName} ${formData.lastName}`,
        language: currentLanguage
      });

      if (paymentResponse.data.success) {
        setIframeToken(paymentResponse.data.token);
        setIframeOrderId(paymentResponse.data.orderId);
        setOidWithExpiry(paymentResponse.data.orderId);
        setIframeLoading(true); // Iframe yükleniyor
        setShowIframe(true);
      } else {
        console.error('Payment provider response:', paymentResponse.data);
        setModalConfig({
          type: 'error',
          message: paymentResponse.data?.error
            || paymentResponse.data?.msg
            || paymentResponse.data?.reason
            || t('paymentRegister.errors.paymentError')
        });
        setShowModal(true);
      }

    } catch (err) {
      console.error('Payment initiation error:', err.response?.data || err);
      setModalConfig({
        type: 'error',
        message: err.response?.data?.error
          || err.response?.data?.msg
          || err.response?.data?.reason
          || t('paymentRegister.errors.paymentInitError')
      });
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = (type) => {
    const pdfUrls = {
      kvkk: '/documents/Kvkk_Sozlesmesi.pdf',
      gdpr: '/documents/GDPRTurkce_Inglizce.pdf'
    };

    try {
      const link = document.createElement('a');
      link.href = pdfUrls[type];
      link.download = `${type.toUpperCase()}_Sozlesmesi.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('File could not be downloaded. Please try again later.');
    }
  };

  function setOidWithExpiry(value) {
    const now = new Date();

    const item = {
      value: value,
      expiry: now.getTime() + 60 * 1000, // expire süresi ms cinsinden
    };

    localStorage.setItem("oid", JSON.stringify(item));
  }

  const formatPrice = (pkg) => {
    // Dil tercihine göre para birimi göster (TR dili = TL, diğerleri = USD)
    if (isTurkishCurrency) {
      return {
        price: `₺${pkg.price.toLocaleString('tr-TR')}`,
        originalPrice: pkg.originalPrice ? `₺${pkg.originalPrice.toLocaleString('tr-TR')}` : null
      };
    } else {
      return {
        price: `$${pkg.usdPrice.toLocaleString('en-US')}`,
        originalPrice: pkg.usdOriginalPrice ? `$${pkg.usdOriginalPrice.toLocaleString('en-US')}` : null
      };
    }
  };

  return (
    <div className="auth-container">
      {/* Background Animation */}
      <div className="bg-animation">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
        <div className="floating-shape shape-6"></div>
        <div className="floating-shape shape-7"></div>
      </div>

      <div className={`payment-register-box ${currentStep === 1 ? 'wide' : ''}`}>
        {/* Logo Section */}
        <div className="logo-section">
          <img
            src={logo}
            alt="SeedXpert Logo"
            className="auth-logo"
          />
        </div>
        <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
          <LanguageSwitcher />
        </div>

        {/* Progress Bar */}
        <div className="progress-bar-container">
          <div className="progress-step-wrapper">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-circle">1</div>
              <span className="step-label">{t('paymentRegister.progressSteps.package')}</span>
            </div>
            <div className={`progress-line ${currentStep >= 2 ? 'active' : ''}`}></div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-circle">2</div>
              <span className="step-label">{t('paymentRegister.progressSteps.register')}</span>
            </div>
            <div className={`progress-line ${currentStep >= 3 ? 'active' : ''}`}></div>
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-circle">3</div>
              <span className="step-label">{t('paymentRegister.progressSteps.payment')}</span>
            </div>
          </div>
        </div>

        {/* Step 1: Package Selection */}
        {currentStep === 1 && (
          <>
            <div className="auth-header">
              <h2>{t('paymentRegister.packageSelection.title')}</h2>
              <p className="auth-subtitle">{t('paymentRegister.packageSelection.subtitle')}</p>
            </div>

            <div className="packages-container">
              {packages.map((pkg) => {
                const priceInfo = formatPrice(pkg);
                return (
                  <div
                    key={pkg.id}
                    className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
                    onClick={() => handlePackageSelection(pkg)}
                  >
                    {pkg.popular && <div className="popular-badge">{t('paymentRegister.packageSelection.popular')}</div>}
                    {pkg.discountBadge && <div className="discount-badge">{pkg.discountBadge}</div>}

                    <h3 className="package-name">{pkg.name}</h3>
                    <p className="package-description">{pkg.description}</p>

                    <div className="package-price">
                      {priceInfo.originalPrice && (
                        <span className="original-price">{priceInfo.originalPrice}</span>
                      )}
                      <span className="current-price">{priceInfo.price} {isTurkishCurrency && <span className="vat-label">{t('paymentRegister.packageSelection.vat')}</span>}</span>
                    </div>
                    {pkg.unitPrice && <p className="unit-price-register">{pkg.unitPrice}</p>}

                    <ul className="package-features">
                      {pkg.features.map((feature, index) => (
                        <li key={index}>✓ {feature}</li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      className={`select-package-btn ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
                    >
                      {selectedPackage?.id === pkg.id ? t('paymentRegister.packageSelection.selected') : t('paymentRegister.packageSelection.select')}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="step-navigation">
              <button
                type="button"
                className="auth-btn primary-btn"
                onClick={handlePackageStepNext}
                disabled={!selectedPackage}
              >
                {t('paymentRegister.packageSelection.continue')}
              </button>
            </div>
            <div className="auth-footer">
              <p className="switch-text">
                {t('paymentRegister.registration.alreadyHaveAccount')}{' '}
                <span className="switch-link" onClick={() => navigate('/')}>
                  {t('paymentRegister.registration.signIn')}
                </span>
              </p>
            </div>
          </>
        )}

        {/* Step 2: Registration Form */}
        {currentStep === 2 && (
          <>


            {/* Selected Package Summary */}
            {selectedPackage && (
              <div className="selected-package-summary">

                <div className="summary-content">
                  <div className="summary-info">
                    <h3>{t('paymentRegister.registration.selectedPackage')}</h3>
                    <span className="package-name">{selectedPackage.name}</span>
                    <span className="package-price">
                      {formatPrice(selectedPackage).price} {isTurkishCurrency && <span style={{ fontSize: '12px', color: '#666' }}>{t('paymentRegister.packageSelection.vat')}</span>}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="change-package-btn"
                    onClick={() => setCurrentStep(1)}
                  >
                    {t('paymentRegister.registration.changePackage')}
                  </button>
                </div>
              </div>
            )}

            <div className="auth-header">
              <h2>{t('paymentRegister.registration.title')}</h2>
              <p className="auth-subtitle">{t('paymentRegister.registration.subtitle')}</p>
            </div>

            <form onSubmit={handleRegisterStep}>
              {/* Name Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('paymentRegister.registration.firstName')}</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('paymentRegister.registration.firstNamePlaceholder')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('paymentRegister.registration.lastName')}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('paymentRegister.registration.lastNamePlaceholder')}
                    required
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('paymentRegister.registration.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('paymentRegister.registration.emailPlaceholder')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('paymentRegister.registration.phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('paymentRegister.registration.phonePlaceholder')}
                    required
                  />
                </div>
              </div>

              {/* Address Fields */}
              {/* <div className="form-group">
                <label>Adres</label>
                <textarea
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleInputChange}
                  placeholder="Tam adresinizi girin (Sokak, No, Mahalle)"
                  required
                />
              </div> */}

              <div className="form-row three-col">
                <div className="form-group">
                  <label>{t('paymentRegister.registration.country')}</label>
                  <select
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Turkey">{t('paymentRegister.countries.turkey')}</option>
                    <option value="Azerbaijan">{t('paymentRegister.countries.azerbaijan')}</option>
                    <option value="United States">{t('paymentRegister.countries.unitedStates')}</option>
                    <option value="United Kingdom">{t('paymentRegister.countries.unitedKingdom')}</option>
                    <option value="Germany">{t('paymentRegister.countries.germany')}</option>
                    <option value="France">{t('paymentRegister.countries.france')}</option>
                    <option value="Canada">{t('paymentRegister.countries.canada')}</option>
                    <option value="Australia">{t('paymentRegister.countries.australia')}</option>
                    <option value="Italy">{t('paymentRegister.countries.italy')}</option>
                    <option value="Spain">{t('paymentRegister.countries.spain')}</option>
                    <option value="Netherlands">{t('paymentRegister.countries.netherlands')}</option>
                    <option value="Russia">{t('paymentRegister.countries.russia')}</option>
                    <option value="China">{t('paymentRegister.countries.china')}</option>
                    <option value="Japan">{t('paymentRegister.countries.japan')}</option>
                    <option value="South Korea">{t('paymentRegister.countries.southKorea')}</option>
                    <option value="Brazil">{t('paymentRegister.countries.brazil')}</option>
                    <option value="Mexico">{t('paymentRegister.countries.mexico')}</option>
                    <option value="India">{t('paymentRegister.countries.india')}</option>
                    <option value="Saudi Arabia">{t('paymentRegister.countries.saudiArabia')}</option>
                    <option value="South Africa">{t('paymentRegister.countries.southAfrica')}</option>
                    <option value="Other">{t('paymentRegister.countries.other')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('paymentRegister.registration.city')}</label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleInputChange}
                    placeholder={t('paymentRegister.registration.cityPlaceholder')}
                    required
                  />
                </div>
              </div>

              {/* Password Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label>{t('paymentRegister.registration.password')}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={t('paymentRegister.registration.passwordPlaceholder')}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('paymentRegister.registration.hidePassword') : t('paymentRegister.registration.showPassword')}
                    >
                      {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('paymentRegister.registration.confirmPassword')}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder={t('paymentRegister.registration.confirmPasswordPlaceholder')}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? t('paymentRegister.registration.hidePassword') : t('paymentRegister.registration.showPassword')}
                    >
                      {showConfirmPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Legal Agreements Section */}
              <div className="legal-agreements-section">
                <h3 className="legal-section-title">{t('paymentRegister.registration.legalAgreements')}</h3>

                {/* KVKK Agreement - Only for Turkey and Azerbaijan */}
                {isTurkey && (
                  <div className="legal-agreement-item">
                    <div className="agreement-header">
                      <div className="agreement-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={kvkkAccepted}
                            onChange={(e) => setKvkkAccepted(e.target.checked)}
                            required
                          />
                          <span className="checkmark"></span>
                          <span className="agreement-title">{t('paymentRegister.registration.kvkkAgreement')}</span>
                        </label>
                      </div>
                      <div className="agreement-actions">
                        <button
                          type="button"
                          className="download-btn"
                          onClick={() => downloadPDF('kvkk')}
                        >
                          📄 {t('paymentRegister.registration.download')}
                        </button>
                        <button
                          type="button"
                          className="view-btn"
                          onClick={() => setShowKvkkModal(true)}
                        >
                          {t('paymentRegister.registration.view')}
                        </button>
                      </div>
                    </div>
                    <p className="agreement-description">
                      {t('paymentRegister.registration.kvkkDescription')}
                    </p>
                  </div>
                )}

                {/* GDPR Agreement - For all countries except Turkey and Azerbaijan */}
                {!isTurkey && (
                  <div className="legal-agreement-item">
                    <div className="agreement-header">
                      <div className="agreement-checkbox">
                        <label>
                          <input
                            type="checkbox"
                            checked={gdprAccepted}
                            onChange={(e) => setGdprAccepted(e.target.checked)}
                            required
                          />
                          <span className="checkmark"></span>
                          <span className="agreement-title">GDPR Agreement</span>
                        </label>
                      </div>
                      <div className="agreement-actions">
                        <button
                          type="button"
                          className="download-btn"
                          onClick={() => downloadPDF('gdpr')}
                        >
                          📄 Download
                        </button>
                        <button
                          type="button"
                          className="view-btn"
                          onClick={() => setShowGdprModal(true)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                    <p className="agreement-description">
                      General Data Protection Regulation - Data processing agreement
                    </p>
                  </div>
                )}

                {/* General Privacy Policy */}
                <div className="legal-agreement-item">
                  <div className="agreement-header">
                    <div className="agreement-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={privacyAccepted}
                          onChange={(e) => setPrivacyAccepted(e.target.checked)}
                          required
                        />
                        <span className="checkmark"></span>
                        <span className="agreement-title">{t('paymentRegister.registration.privacyAgreement')}</span>
                      </label>
                    </div>
                    <div className="agreement-actions">
                      <button
                        type="button"
                        className="view-btn"
                        onClick={() => setShowPrivacyModal(true)}
                      >
                        {t('paymentRegister.registration.view')}
                      </button>
                    </div>
                  </div>
                  <p className="agreement-description">
                    {t('paymentRegister.registration.privacyDescription')}
                  </p>
                </div>
              </div>

              <div className="step-navigation">
                <button
                  type="button"
                  className="auth-btn secondary-btn"
                  onClick={() => setCurrentStep(1)}
                >
                  {t('paymentRegister.registration.back')}
                </button>
                <button type="submit" className="auth-btn primary-btn">
                  {t('paymentRegister.registration.continue')}
                </button>
              </div>
            </form>

            <div className="auth-footer">
              <p className="switch-text">
                {t('paymentRegister.registration.alreadyHaveAccount')}{' '}
                <span className="switch-link" onClick={() => navigate('/')}>
                  {t('paymentRegister.registration.signIn')}
                </span>
              </p>
            </div>
          </>
        )}

        {/* Step 3: Payment */}
        {currentStep === 3 && (
          <>
            <div className="auth-header">
              <h2>{t('paymentRegister.payment.title')}</h2>
              <p className="auth-subtitle">{t('paymentRegister.payment.subtitle')}</p>
            </div>

            {isLoading && (
              <div className="loading-container">
                <span className="loading-spinner"></span>
                <p>{t('paymentRegister.payment.preparing')}</p>
              </div>
            )}

            {paymentComplete ? (
              <div className="success payment-complete-message">
                <strong>{currentLanguage.startsWith('tr') ? 'Ödeme başarılı!' : 'Payment successful!'}</strong>
                <p style={{ fontSize: '14px', marginTop: '8px' }}>
                  {currentLanguage.startsWith('tr')
                    ? 'Hesabınız etkinleştirildi. Hoş geldiniz e-postası gönderildi.'
                    : 'Your account is active. A welcome email has been sent.'}
                </p>
                <button className="auth-btn primary-btn" onClick={() => navigate('/') }>
                  {t('login')}
                </button>
              </div>
            ) : (
              <>
                {success && (
                  <div className="success">
                    <strong>{t('paymentRegister.payment.success')}</strong>
                    <p style={{ fontSize: '14px', marginTop: '8px' }}>{success}</p>
                  </div>
                )}

                {showIframe && (
                  <div className="payment-iframe-container" style={{ position: 'relative', minHeight: '600px' }}>
                    {iframeLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255, 255, 255, 0.95)',
                        zIndex: 10,
                        gap: '15px'
                      }}>
                        <span className="loading-spinner"></span>
                        <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>
                          {t('paymentRegister.payment.loadingPaymentPage')}
                        </p>
                      </div>
                    )}
                    <iframe
                      src={`https://www.paytr.com/odeme/guvenli/${iframeToken}`}
                      id="paytriframe"
                      frameBorder="0"
                      scrolling="yes"
                      style={{ width: '100%', height: '600px' }}
                      onLoad={() => setIframeLoading(false)}
                    ></iframe>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Modals - KVKK, GDPR, Privacy */}
      {/* KVKK Modal */}
      {showKvkkModal && (
        <div className="modal-overlay legal-modal" onClick={() => setShowKvkkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('paymentRegister.modals.kvkk.title')}</h2>
              <button className="close-btn" onClick={() => setShowKvkkModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="legal-document">
                <h3>{t('paymentRegister.modals.kvkk.heading')}</h3>
                <p><strong>{t('paymentRegister.modals.kvkk.dataController')}</strong> {t('paymentRegister.modals.kvkk.dataControllerName')}</p>
                <p>{t('paymentRegister.modals.kvkk.intro')}</p>

                <h4>{t('paymentRegister.modals.kvkk.collectedDataTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.kvkk.collectedData', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h4>{t('paymentRegister.modals.kvkk.processingPurposesTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.kvkk.processingPurposes', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h4>{t('paymentRegister.modals.kvkk.dataSubjectRightsTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.kvkk.dataSubjectRights', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button className="download-btn" onClick={() => downloadPDF('kvkk')}>
                {t('paymentRegister.modals.kvkk.downloadPdf')}
              </button>
              <button className="close-btn-secondary" onClick={() => setShowKvkkModal(false)}>
                {t('paymentRegister.modals.kvkk.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GDPR Modal */}
      {showGdprModal && (
        <div className="modal-overlay legal-modal" onClick={() => setShowGdprModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('paymentRegister.modals.gdpr.title')}</h2>
              <button className="close-btn" onClick={() => setShowGdprModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="legal-document">
                <h3>{t('paymentRegister.modals.gdpr.heading')}</h3>
                <p><strong>{t('paymentRegister.modals.gdpr.dataController')}</strong> {t('paymentRegister.modals.gdpr.dataControllerName')}</p>
                <p>{t('paymentRegister.modals.gdpr.intro')}</p>

                <h4>{t('paymentRegister.modals.gdpr.collectedDataTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.gdpr.collectedData', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h4>{t('paymentRegister.modals.gdpr.processingPurposesTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.gdpr.processingPurposes', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <h4>{t('paymentRegister.modals.gdpr.dataSubjectRightsTitle')}</h4>
                <ul>
                  {t('paymentRegister.modals.gdpr.dataSubjectRights', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button className="download-btn" onClick={() => downloadPDF('gdpr')}>
                {t('paymentRegister.modals.gdpr.downloadPdf')}
              </button>
              <button className="close-btn-secondary" onClick={() => setShowGdprModal(false)}>
                {t('paymentRegister.modals.gdpr.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="modal-overlay legal-modal" onClick={() => setShowPrivacyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('paymentRegister.modals.privacy.title')}</h2>
              <button className="close-btn" onClick={() => setShowPrivacyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="legal-document">
                <h3>{t('paymentRegister.modals.privacy.heading')}</h3>
                {t('paymentRegister.modals.privacy.sections', { returnObjects: true }).map((section, index) => (
                  <div key={index}>
                    <h4>{section.title}</h4>
                    <p>{section.content}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="close-btn-secondary" onClick={() => setShowPrivacyModal(false)}>
                {t('paymentRegister.modals.privacy.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Modal */}
      {showModal && (
        <div className="modal-overlay error-success-modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`modal-icon ${modalConfig.type}`}>
              {modalConfig.type === 'success' ? '✓' : '✕'}
            </div>
            <p className="modal-message">{modalConfig.message}</p>
            <button className="modal-close-btn" onClick={() => setShowModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRegister;
